import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  Icon,
  Input,
  Kbd,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  VisualPicker,
  toColor,
  useMenuFocusStealGuard,
  type Color,
  type VisualPickerAction,
} from "@salestrak/ui";

import type { StageOption } from "#app/lib/data";

type StageInlineEditorProps = {
  stage: StageOption;
  onRename: (stageId: string, name: string) => void;
  onRecolor: (stage: StageOption, color: Color) => void;
  /** Opens the workflow's stage manager dialog, from the palette's footer. */
  onOpenSettings?: (() => void) | undefined;
  /**
   * Renders an explicit ✕ before the ✓ — the settings rows' visible cancel.
   * The board/list headers stay ✓-only: their edit row is a takeover of a
   * 28px header with no room for a second control, and Escape already cancels.
   */
  showCancel?: boolean;
  /** Fires on every way out: commit, Escape, cancel, and the settings deep-link. */
  onExit: () => void;
};

/**
 * The inline stage edit row, shared by the board's stage column headers, the
 * list's stage group headers, and the workflow Stages settings rows: an
 * autofocused select-all input on a primary-ringed field, ✓/Enter commit,
 * Escape cancel, blur commits — with a solid square swatch of the stage's
 * color as the color-picker trigger (the reference grammar: while editing,
 * the swatch names the recolor affordance louder than the stage glyph
 * would), applying each pick immediately.
 */
export function StageInlineEditor({
  stage,
  onRename,
  onRecolor,
  onOpenSettings,
  showCancel,
  onExit,
}: StageInlineEditorProps) {
  const [draft, setDraft] = useState(stage.name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Pressing the swatch (and working inside its popover) blurs the input, and
  // blur is the commit gesture — so the palette holds the edit row open.
  const holdEditRef = useRef(false);
  // The list's group menu Rename mounts this row, then the closing menu hands
  // focus back to its "…" trigger. That blur is the menu's, not the user's.
  // Mounted only while editing, so the mount is the start of the edit.
  const reclaimFromMenuSteal = useMenuFocusStealGuard(true);

  const exitEditing = useCallback(() => {
    holdEditRef.current = false;
    onExit();
  }, [onExit]);

  const commit = useCallback(() => {
    const name = draft.trim();
    if (name && name !== stage.name) onRename(stage.stageId, name);
    exitEditing();
  }, [draft, stage.name, stage.stageId, onRename, exitEditing]);

  const handleColorOpenChange = useCallback((open: boolean) => {
    holdEditRef.current = open;
    // Palette closed — hand focus back so the edit row keeps its
    // commit-on-blur contract.
    if (!open) inputRef.current?.focus();
  }, []);

  // The picker closes itself before running a footer row, so the press never
  // reaches the dismiss layer.
  const handleOpenSettings = useCallback(() => {
    exitEditing();
    onOpenSettings?.();
  }, [onOpenSettings, exitEditing]);

  const paletteActions = useMemo<VisualPickerAction[] | undefined>(
    () =>
      onOpenSettings
        ? [{ icon: "settings", label: "Edit stages", onClick: handleOpenSettings }]
        : undefined,
    [onOpenSettings, handleOpenSettings],
  );

  return (
    // Coherent chrome: the xs padding + 30px height leave the same 4px
    // gutter on every side of the 20px squared actions, and the ring's 8px
    // radius (`lg`) wraps their corners concentrically.
    <Box
      direction="row"
      align="center"
      gap="xs"
      flex={1}
      shrink
      minHeight={30}
      rounded="lg"
      background="background"
      border
      borderColor="primary"
      paddingX="xs"
    >
      {/* The library's one color popup (a bottom sheet on a phone), opened
          from a solid square of the stage's color rather than the stage
          glyph — the swatch IS the recolor affordance, and it repaints live
          as picks apply. */}
      <VisualPicker
        actions={paletteActions}
        color={toColor(stage.icon?.color) ?? "teal"}
        onColorChange={(color) => onRecolor(stage, color)}
        onOpenChange={handleColorOpenChange}
        tooltip="Change color"
        trigger={
          <Button
            variant="ghost"
            size="icon-xs"
            shape="square"
            aria-label="Stage color"
            onPointerDown={() => {
              holdEditRef.current = true;
            }}
          >
            <ColorSwatch color={toColor(stage.icon?.color) ?? "teal"} />
          </Button>
        }
      />

      {/* The column flex box stretches the bare input to the row's free
          width, so the ✓ sits at the ring's right edge whatever the name's
          length. `bare` so the row's own ring and height are the only
          chrome — an input shell inside it would be a second border on one
          control. */}
      <Box flex={1} shrink minWidth={0} direction="column" justify="center">
        <Input
          ref={inputRef}
          aria-label="Stage name"
          autoFocus
          bare
          // `bare` sheds the shell's type ramp, so the raw input falls to the
          // UA's 16px — restate the headers' 14px rung. (className = gap.)
          className="text-sm"
          value={draft}
          placeholder="Stage name"
          onChange={(event) => setDraft(event.currentTarget.value)}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={(event) => {
            if (reclaimFromMenuSteal(event)) return;
            if (holdEditRef.current) return;
            commit();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
            if (event.key === "Escape") exitEditing();
          }}
        />
      </Box>

      {showCancel ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                shape="square"
                aria-label="Cancel stage edit"
                // Pressing cancel blurs the input, and blur is the commit
                // gesture — hold the edit open through the blur so the click
                // can discard it.
                onPointerDown={() => {
                  holdEditRef.current = true;
                }}
                onClick={exitEditing}
              />
            }
          >
            <Icon name="x" size="md" color="muted" />
          </TooltipTrigger>
          <TooltipPopup side="top">
            <Box direction="row" align="center" gap="sm">
              Cancel
              <Kbd>Esc</Kbd>
            </Box>
          </TooltipPopup>
        </Tooltip>
      ) : null}
      {/* The value-action chip treatment (squared `outline`, opaque
          `bg-background` + border) — a bordered box reads as the commit
          control where the old muted fill washed out on the light field. */}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon-xs"
              shape="square"
              aria-label="Save stage name"
              onClick={commit}
            />
          }
        >
          <Icon name="check" size="md" />
        </TooltipTrigger>
        <TooltipPopup side="top">
          <Box direction="row" align="center" gap="sm">
            Save
            <Kbd>Enter</Kbd>
          </Box>
        </TooltipPopup>
      </Tooltip>
    </Box>
  );
}
